<template>
  <!-- eslint-disable max-len -->
  <div class="form-group">
    <label v-if="labelHtml" :for="_uid" v-html="$slots.default && $slots.default[0].text || ''"></label>
    <label v-else-if="label" :for="_uid">{{ $slots.default && $slots.default[0].text || '' }}</label>
    <div :class="{ 'input-group': remove || to }">
      <select
        :style="!disabled ? `background-color: ${dropdownBackground}; color: ${dropdownForeground}; border: 1px solid ${borderColor}; border-radius: 2px;` : ''"
        :value="value"
        @change="onChange"
        class="form-control"
        :class="{ error: validator && validator.$error }"
        @focus="onFocus"
        @mousedown="onClick"
        @blur="onBlur"
        :disabled="disabled"
        v-focus="(value) ? false : autofocus"
        :id="_uid"
        :size="selectSize"
        :ref="_uid"
      >
        <option
          v-if="defaultValue == null || defaultValue === ''"
          :style="`background-color: ${dropdownOptionBackground || dropdownBackground}; color: ${dropdownOptionForeground || dropdownForeground};`"
          value=""
        >
          <template v-if="placeholder"><div v-html="placeholder"></div></template>
          <template v-else>Select {{ $slots.default[0].text }}</template>
        </option>
        <template v-if="!(optimized && !(brokenBecauseSafari)) || focused">
          <option
            v-for="option in sortedOptions"
            v-show="(option === selectedOption) || !filter || !(filter.has(getKey(option)))"
            :style="`background-color: ${dropdownOptionBackground || dropdownBackground}; color: ${dropdownOptionForeground || dropdownForeground};`"
            :key="getKey(option)"
            :value="getKey(option)"
            :selected="value !== '' && value != null && getKey(option) == value"
          >
            <template v-if="optionsHtml"><div v-html="getDisplayValue(option)"></div></template>
            <template v-else>{{ getDisplayValue(option) }}</template>
          </option>
        </template>
        <template v-else>
          <option
            :style="`background-color: ${dropdownOptionBackground || dropdownBackground}; color: ${dropdownOptionForeground || dropdownForeground};`"
            :key="value"
            :value="value"
            :selected="value !== '' && value != null"
          >
            <template v-if="optionsHtml"><div v-html="getDisplayValue(selectedOption)"></div></template>
            <template v-else>{{ getDisplayValue(selectedOption) }}</template>
          </option>
        </template>
      </select>
      <div v-if="to && value && to(value)" class="input-group-append mr-2">
        <a
          class="btn btn-sm"
          target="_blank"
          :href="to(value)"
          style="line-height: unset;">
          Info
        </a>
      </div>
      <div v-if="!disabled && remove" class="input-group-append">
        <button
          class="btn btn-sm"
          @click.prevent="remove">
          X
        </button>
      </div>
      <div v-if="!disabled && down" class="input-group-append">
        <button
          class="btn btn-sm"
          @click.prevent="down">
          &#9660;
        </button>
      </div>
      <div v-if="!disabled && up" class="input-group-append">
        <button
          class="btn btn-sm"
          @click.prevent="up">
          &#9650;
        </button>
      </div>
    </div>
    <template v-if="validator && validator.$error">
      <p class="error-msg" v-if="required && !validator.required"><slot>Field</slot> is required</p>
      <p class="error-msg" v-else-if="customError">{{ customError.message }}</p>
    </template>
    <!-- <p class="error-msg">{{ value }}</p> -->
  </div>
</template>

<script>
export default {
  props: {
    autofocus: { type: Boolean },
    customErrors: { type: Array, default: () => [] },
    disabled: { type: Boolean },
    display: { type: [String, Function] },
    down: { type: Function },
    label: { type: Boolean },
    labelHtml: { type: Boolean },
    placeholder: { type: String },
    optionsHtml: { type: Boolean },
    options: { type: Array, default: () => [] },
    remove: { type: Function },
    required: { type: Boolean },
    to: { type: Function },
    up: { type: Function },
    use: { type: String },
    /**
     * This makes it so <option> elements are generated only when the select is focused.
     * NOTE: because this requires https://caniuse.com/mdn-api_htmlselectelement_showpicker
     * using `optimized` means Safari won't be able to use the select.
     */
    optimized: { type: Boolean },
    filter: { type: Set, default: null },
    disableSort: { type: Boolean },
    sortByKey: { type: Boolean },
    disableChangeMappedToInput: { type: Boolean },
    integer: { type: Boolean },
    boolean: { type: Boolean },
    validator: { type: Object },
    value: { type: [String, Number, Boolean], default: '' },
    defaultValue: { type: [String, Number, Boolean], default: '' },
    dropdownBackground: { type: String, default: 'white' },
    dropdownForeground: { type: String, default: 'black' },
    dropdownOptionBackground: { type: String, default: 'white' },
    dropdownOptionForeground: { type: String, default: 'black' },
    borderColor: { type: String, default: 'lightgrey' }
  },
  data() {
    return {
      focused: false,
      selectSize: undefined,
    };
  },
  computed: {
    customError() {
      return (this.customErrors || []).find(error => !this.validator[error.validator]);
    },
    clearedValue() {
      return (this.defaultValue != null && this.defaultValue !== '') ? this.defaultValue : null;
    },
    selectedOption() {
      return this.value ? (this.options.find(o => this.getKey(o) === this.value) || this.clearedValue) : this.clearedValue;
    },
    sortedOptions() {
      if (this.disableSort) {
        return this.options;
      }
      return this.options.slice().sort((a, b) => {
        const aSortValue = this.sortByKey ? this.getKey(a) : this.getDisplayValue(a);
        const bSortValue = this.sortByKey ? this.getKey(b) : this.getDisplayValue(b);
        return aSortValue < bSortValue ? -1 : aSortValue > bSortValue ? 1 : 0;
      });
    },
    iOS() {
      const platform = navigator.platform || navigator.userAgentData.platform;
      return [
        'iPad Simulator',
        'iPhone Simulator',
        'iPod Simulator',
        'iPad',
        'iPhone',
        'iPod'
      ].includes(platform)
      // iPad on iOS 13 detection
      || (navigator.userAgent.includes('Mac') && 'ontouchend' in document);
    },
    isSafari() {
      return /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    },
    brokenBecauseSafari() {
      return this.iOS || this.isSafari;
    },
  },
  methods: {
    onChange($event) {
      // Fix for Edge: map change to input
      let eventVal = $event.target.value;
      if (this.boolean) {
        eventVal = JSON.parse(eventVal);
      } else if (this.integer) {
        eventVal = parseInt($event.target.value, 10);
      }
      this.$emit('input', eventVal);
      this.$emit('change', $event);
    },
    async onClick($event) {
      if (this.optimized && !(this.brokenBecauseSafari)) {
        $event.preventDefault();
        this.focused = true;
        // console.log(this.$refs[this._uid]);
        this.selectSize = 10;
        await this.$nextTick();
        this.selectSize = undefined;
        await this.$nextTick();
        this.$refs[this._uid].showPicker();
      }
    },
    async onFocus() {
      this.focused = true;
    },
    onBlur($event) {
      if (this.validator) this.validator.$touch();
      this.$emit('blur', $event);
      this.focused = false;
      this.selectSize = undefined;
    },
    getDisplayValue(option) {
      if (option == null || option === '') return '';
      if (this.display) {
        if (typeof this.display === 'function') return this.display(option);
        return option[this.display];
      }
      if (this.use) return option[this.use];
      return option;
    },
    getKey(option) {
      return this.use ? option[this.use] : option;
    },
    checkOptions(newValue) {
      // if value is set but matches none of the options, clear it out
      // eslint-disable-next-line no-unneeded-ternary
      const currentOptions = newValue ? newValue : this.options;
      const { clearedValue } = this;
      if (this.value !== clearedValue && currentOptions && currentOptions.length > 0
        && currentOptions.every(o => this.getKey(o) !== this.value)) {
        this.$emit('input', clearedValue);
      }
    },
  },
  watch: {
    options(newValue) {
      // if value is set but matches none of the options, clear it out
      this.checkOptions(newValue);
    },
    value(newValue) {
      // if value is set but matches none of the options,
      // and default is specified that does, set value to default
      if (this.options && this.options.length > 0 && (this.defaultValue != null && this.defaultValue !== '')
        && this.options.every(o => this.getKey(o) !== newValue)
        && this.options.some(o => this.getKey(o) === this.defaultValue)) {
        this.$emit('input', this.defaultValue);
      }
    }
  },
  mounted() {
    this.checkOptions();
  },
};
</script>

<style lang="scss" scoped>
.form-control:hover {
  cursor: pointer;
  box-shadow: 1px 2px 5px rgba(0, 0, 7, 0.24);
}
</style>



// WEBPACK FOOTER //
// src/components/ui/inputs/SelectInput.vue